import featuredSpecialists from '@/data/featuredSpecialists.json';
import { withoutEmailAddressesInLocation } from '@/lib/consultants/locationPrivacy';
import {
  isDoctorBookableForWidget,
  mapDoctorToBookingDoctor,
} from '@/features/booking/utils/mapDoctorToBookingDoctor';
import generatedDoctors from '@/generated/doctors.json';
import type { Doctor, DoctorCard } from '@/types/doctor';
import type { BriefDoctor } from '@/types/doctor-v2';

export type { Doctor, DoctorCard };

interface RawIndexEntry extends BriefDoctor {}

const toDoctorCard = (raw: RawIndexEntry): DoctorCard => ({
  slug: raw.slug,
  fullName: raw.fullName ?? '',
  heading: raw.heading ?? undefined,
  description: raw.description ?? undefined,
  primarySpeciality: raw.primarySpeciality ?? undefined,
  specialities: raw.specialities ?? [],
  assistantPhoneNumber: raw.assistantPhoneNumber ?? undefined,
  gender: raw.gender,
  metaKeywords: [],
  apiId: raw.apiId ?? null,
  primaryPrice: raw.primaryPrice ?? null,
  bookingBufferEnabled: raw.bookingBufferEnabled,
  bookingBufferHours: raw.bookingBufferHours,
  earliestBookingTime: raw.earliestBookingTime,
  serviceAccount: raw.serviceAccount,
  image: raw.image
    ? {
        title: undefined,
        url: raw.image.url,
        width: null,
        height: null,
        contentType: null,
      }
    : undefined,
});

const addBookingFieldsToDoctorCard = async (
  card: DoctorCard,
): Promise<DoctorCard> => {
  const detail = await getDoctorDetail(card.slug);

  if (!detail) {
    return card;
  }

  if (!detail.image) {
    const brief = (generatedDoctors as unknown as RawIndexEntry[]).find(
      (item) => item.slug === card.slug,
    );
    if (brief?.image?.url) {
      detail.image = {
        title: undefined,
        url: brief.image.url,
        width: null,
        height: null,
        contentType: null,
      };
    }
  }

  const bookingDoctor = mapDoctorToBookingDoctor(detail);

  return {
    ...card,
    apiId: detail.apiId ?? null,
    categoryId: detail.categoryId ?? null,
    primaryPrice:
      detail.primaryPrice !== null && detail.primaryPrice !== undefined
        ? String(detail.primaryPrice)
        : card.primaryPrice,
    duration:
      detail.duration !== null && detail.duration !== undefined
        ? String(detail.duration)
        : null,
    bookingBufferEnabled: detail.bookingBufferEnabled,
    bookingBufferHours: detail.bookingBufferHours,
    earliestBookingTime: detail.earliestBookingTime,
    serviceAccount: detail.serviceAccount === true,
    doctifySlug: detail.doctifySlug ?? null,
    bookingDoctor,
    isBookable:
      detail.bookable === true && isDoctorBookableForWidget(bookingDoctor),
    languages: (detail.languages ?? []) as DoctorCard['languages'],
    primaryLocations: (detail.primaryLocations ?? []).map(
      withoutEmailAddressesInLocation,
    ) as DoctorCard['primaryLocations'],
    privateLocations: (detail.privateLocations ?? []).map(
      withoutEmailAddressesInLocation,
    ) as DoctorCard['privateLocations'],
  };
};

export const getDoctorsList = (): DoctorCard[] => {
  const raw = generatedDoctors as unknown as RawIndexEntry[];
  const seen = new Map<string, DoctorCard>();
  for (const item of raw) {
    if (!seen.has(item.slug)) {
      seen.set(item.slug, toDoctorCard(item));
    }
  }
  return Array.from(seen.values());
};

export const getDoctorsListWithBookingFields = async (): Promise<
  DoctorCard[]
> => {
  return Promise.all(getDoctorsList().map(addBookingFieldsToDoctorCard));
};

/**
 * Resolves booking fields for only the doctors matching the given apiIds,
 * instead of hydrating the entire doctor list (`getDoctorsListWithBookingFields`)
 * and filtering afterwards. Used by the booking widget's `apiIds` source mode,
 * where callers almost always want just one or two specific doctors/clinics.
 */
export const getDoctorsByApiIds = async (
  apiIds: string[],
): Promise<DoctorCard[]> => {
  const wanted = new Set(apiIds.map(String));
  const matches = getDoctorsList().filter(
    (card) => card.apiId && wanted.has(String(card.apiId)),
  );
  return Promise.all(matches.map(addBookingFieldsToDoctorCard));
};

const isDoctor =
  (slug: string) =>
  (value: unknown): value is Doctor => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'slug' in value &&
      (value as { slug: string }).slug === slug
    );
  };

export const getDoctorDetail = async (slug: string): Promise<Doctor | null> => {
  const matchSlug = isDoctor(slug);

  try {
    const doctorModule = await import(`@/data/doctors/${slug}.ts`);

    const doctor = Object.values(doctorModule).find(matchSlug);
    if (doctor) return doctor;
  } catch {
    // Not found in .ts files
  }

  try {
    const clinicModule = await import(`@/data/clinics/${slug}`);
    const clinic = Object.values(clinicModule).find(matchSlug);
    if (clinic) return clinic;
  } catch {
    console.warn('[getDoctorDetail] Doctor not found for slug:', slug);
    return null;
  }

  return null;
};

export const getFeaturedDoctorSlugs = (): string[] => {
  return featuredSpecialists;
};
